import {
  PMS_BOARD_LABELS,
  PMS_GUARANTEE_LABELS_FR,
  PMS_STATUS_LABELS_FR,
  pmsNightCount,
  pmsPartialStatusLabelFr,
} from '@lemontaigu/pms-core';
import { BOARD_LONG_FR, type RackStayMoney } from './rackMoney.js';
import type { ReservationView, RoomStayView } from '@lemontaigu/pms-core';
import { bedConfigsLabel, fmtDate, fmtDayShort, fmtEuros, outlookLabel, type RoomConfigRow } from './logic.js';
import type { RackPlanView } from './rackPlan.js';
import { PAYMENT_STATE_LABEL_FR, amountText, paymentSummary, paymentTitle, type AdvanceRead } from './advanceView.js';
import { planGapNote, planGapText, planGapTitle, type PlanGapRead } from './planGapView.js';
import { roomLineMark } from './roomLineView.js';

// The rack's detail pane — « Inspecteur ».
//
// WHY IT IS DOCKED TO THE RIGHT AND NOT BELOW OR OVER. Measured in a browser
// at 1600x1000 with the real stylesheet, 42 units x 25 days: a 330px pane
// docked right costs ZERO rows and ZERO days (the day columns go 55px → 42px,
// so only the bar captions shorten). The same area docked at the BOTTOM costs
// about eight units of vertical space — which is the exact resource the density
// work exists to buy back. An overlay costs the context entirely.
//
// So the grid never moves when you click it. That is the whole requirement:
// at 25 days across, a panel that reflowed the rack would lose your place on
// every click, and finding your place again is most of what the desk is doing.
//
// It is a pure renderer: everything it shows is resolved by `Rack` and handed
// down. It names no service and performs no lookup (docs/ARCHITECTURE.md).

/** What the last click selected. A free cell and a strip click are the SAME
 *  kind — a unit, with or without a night attached — because the facts about a
 *  room do not change when you also name a date; only the offered action does. */
export type RackSelection =
  | {
      kind: 'stay';
      stay: RoomStayView;
      /** null when the booking a drawn stay belongs to is not loaded — the
       *  rack already refuses to OPEN such a stay, and must not pretend here. */
      reservation: ReservationView | null;
      /** The booking's OTHER logements. A two-room booking that showed only the
       *  room you clicked would answer the wrong question. */
      siblings: RoomStayView[];
      /** « Booking.com », « Manuel — KRONCYCLING » — null when the booking is
       *  not loaded. The Planning's origin bucket, in words. */
      origin: string | null;
      /** « Lit demandé », worded, and whether the ASSIGNED room stands otherwise. */
      bed: { text: string; warn: boolean };
      /** The channel's arrival window, « 18h–19h »; null when none was given. */
      arrival: string | null;
      /** Proposals — only for a stay with no unit. `unavailable` when the rack
       *  has not read every night of the stay (a read in flight, or a stay
       *  longer than one read may be): ranked on unread nights, a room would
       *  look free that is not. */
      tetris: RackTetris | { unavailable: string } | null;
    }
  | { kind: 'room'; row: RoomConfigRow; date: string | null }
  | { kind: 'upgrades'; review: RackUpgradeReview };

/** « Surclassements de demain » (brief §6.4), resolved for display. */
export interface RackUpgradeReview {
  /** The arrivals day reviewed — tomorrow. */
  date: string;
  lines: { stayId: string; text: string; freeAfter: number }[];
  /** Why the list may be short: arrivals not read in full, say. */
  note: string | null;
  keepFree: number;
  previewStayId: string | null;
  onPreview: (stayId: string) => void;
  vetoed: { stayId: string; name: string }[];
  onVeto: (stayId: string) => void;
  onClearVetoes: () => void;
}

/** « Tetris » v2's answer when no room is free throughout, and the controls
 *  that steer it. */
export interface RackPlanControl {
  view: RackPlanView;
  /** Shows the next of the (at most three) plans; null when there is one. */
  onNext: (() => void) | null;
  /** « Ne pas déplacer » — re-plans without moving that stay. */
  onVeto: (stayId: string) => void;
  vetoed: { stayId: string; name: string }[];
  onClearVetoes: () => void;
}

/** « Tetris » v1, resolved for display: the ranked rooms, each with its reasons
 *  already worded. Advisory — the desk allocates in Vega until cutover. */
export interface RackTetris {
  lines: {
    roomId: number;
    roomNumber: string;
    reasons: string[];
    /** The proposal carries a cost worth weighing (another category, a
     *  stranded night, a bed to change). */
    warn: boolean;
    /** The unit is hidden by the rack's filter, so its ghost cannot show. */
    hidden: boolean;
  }[];
  /** Feasible rooms beyond those listed. */
  more: number;
  /** Why rooms of the booked category were turned away; null = none were. */
  rejectedText: string | null;
  /** The room the grid is ghosting. */
  previewRoomId: number | null;
  onPreview: (roomId: number) => void;
  /** v2, when v1 found no room free throughout. */
  plan: RackPlanControl | null;
}

export interface RackInspectorProps {
  selection: RackSelection;
  /** The picked stay's money, from its booking's charge lines: null while the
   *  read is in flight, a message when it failed. */
  money?: RackStayMoney | string | null;
  /** What the picked booking has paid (« Arrhes », other payments, its status):
   *  null while the read is in flight, a message when it failed. */
  advance?: AdvanceRead;
  /** Where Vega's charges and its rooming list disagree for the picked booking:
   *  null while reading or when there is nothing to say, a message when it failed. */
  planGap?: PlanGapRead;
  /** Opens the full booking form. Absent for a stay whose booking is missing. */
  onOpen: (() => void) | null;
  onClose: () => void;
}

const Field = ({ k, v, title }: { k: string; v: string; title?: string | null }) => (
  <span className="pms-insp-f" title={title ?? undefined}>
    <span className="pms-insp-k">{k}</span>
    <span className="pms-insp-v">{v}</span>
  </span>
);

/** « 2 adultes · 1 enfant · 1 bébé » — only the counts that are non-zero, so
 *  the ordinary booking reads « 2 adultes » and not a row of zeros. */
function peopleFr(adults: number, children: number, infants: number): string {
  const parts = [`${adults} adulte${adults > 1 ? 's' : ''}`];
  if (children > 0) parts.push(`${children} enfant${children > 1 ? 's' : ''}`);
  if (infants > 0) parts.push(`${infants} bébé${infants > 1 ? 's' : ''}`);
  return parts.join(' · ');
}

/** Siblings sharing dates and status, room numbers in numeric order. */
function siblingGroups(siblings: readonly RoomStayView[]) {
  const groups = new Map<string, { key: string; checkin: string; checkout: string; status: RoomStayView['status']; rooms: string[] }>();
  for (const s of siblings) {
    const key = `${s.checkinDate}|${s.checkoutDate}|${s.status}`;
    const g = groups.get(key) ?? { key, checkin: s.checkinDate, checkout: s.checkoutDate, status: s.status, rooms: [] };
    g.rooms.push(s.roomNumber ?? 'à attribuer');
    groups.set(key, g);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, rooms: g.rooms.sort((a, b) => a.localeCompare(b, 'fr', { numeric: true })) }))
    .sort((a, b) => a.checkin.localeCompare(b.checkin) || b.rooms.length - a.rooms.length);
}

export function RackInspector({ selection, money = null, advance = null, planGap = null, onOpen, onClose }: RackInspectorProps) {
  return (
    <aside className="pms-insp" aria-label="Détail de la sélection">
      {selection.kind === 'stay'
        ? stayBody(selection, money, advance, planGap, onOpen, onClose)
        : selection.kind === 'upgrades'
          ? upgradesBody(selection.review, onClose)
          : roomBody(selection, onOpen, onClose)}
    </aside>
  );
}

function head(title: string, sub: string, tag: string, onClose: () => void) {
  return (
    <header className="pms-insp-head">
      <b>{title}</b>
      <span className="pms-insp-sub">{sub}</span>
      <span className="pms-insp-tag">{tag}</span>
      <button type="button" className="pms-insp-close" onClick={onClose} aria-label="Fermer le détail">
        ✕
      </button>
    </header>
  );
}

function paymentSection(advance: AdvanceRead, totalCents: number) {
  const title = paymentTitle(advance, totalCents);
  const s = advance === null || typeof advance === 'string' ? null : paymentSummary(advance, totalCents);
  return (
    <div className="pms-insp-sec pms-insp-grid" aria-label="Paiements">
      <Field k="Arrhes" v={amountText(advance, (x) => x.arrhesCents, totalCents)} title={title} />
      {s !== null && s.otherCents !== 0 && (
        <Field k="Autres règlements" v={amountText(advance, (x) => x.otherCents, totalCents)} title={title} />
      )}
      <Field
        k="Réglé / total"
        v={s === null ? amountText(advance, (x) => x.paidCents, totalCents) : `${fmtEuros(s.paidCents)} / ${fmtEuros(totalCents)}`}
        title={title}
      />
      {s?.state != null && (
        <span className="pms-insp-f" title={title ?? undefined}>
          <span className="pms-insp-k">Paiement</span>
          <span className={`pms-insp-chip ${s.state === 'paid' ? 'is-ok' : 'is-warn'}`}>
            {PAYMENT_STATE_LABEL_FR[s.state]}
          </span>
        </span>
      )}
    </div>
  );
}

function stayBody(
  sel: Extract<RackSelection, { kind: 'stay' }>,
  money: RackStayMoney | string | null,
  advance: AdvanceRead,
  planGap: PlanGapRead,
  onOpen: (() => void) | null,
  onClose: () => void,
) {
  const { stay, reservation: r, siblings } = sel;
  const guest = r?.guest ?? null;
  // The OCCUPANT where one was named, else the booker — the same rule the bar
  // caption uses, so the pane and the bar you clicked agree about who this is.
  const who = stay.occupantName ?? `${(guest?.lastName ?? '?').toUpperCase()} ${guest?.firstName ?? ''}`.trim();
  // A multi-room booking gets the partial label (« Partiellement arrivée ») —
  // the stay's own status is the truth for THIS room, the booking's is not.
  const partial = siblings.length === 0 ? null : pmsPartialStatusLabelFr([stay, ...siblings]);

  const nights = pmsNightCount(stay.checkinDate, stay.checkoutDate);
  const roomCount = siblings.length + 1;
  const m = typeof money === 'object' ? money : null;
  // « 75,00 € /pers. » for a group priced per person, « 110,00 € ×5 » for rooms Vega left on this one (roomLineView)
  const mark = m?.nightCents == null || m.nightQuantity === null ? null : roomLineMark(m.nightCents.min, m.nightQuantity, stay.adults + stay.children);
  const nightText =
    m?.nightCents == null
      ? null
      : m.nightCents.min === m.nightCents.max
        ? `${fmtEuros(m.nightCents.min)}${mark?.suffix ?? ''}`
        : `${fmtEuros(m.nightCents.min)} à ${fmtEuros(m.nightCents.max)}${mark?.suffix ?? ''}`;

  return (
    <>
      {head(who, stay.roomNumber ?? 'à attribuer', PMS_STATUS_LABELS_FR[stay.status], onClose)}

      {/* The band and chips of the reservation pop-up (ReservationForm), at the
          pane's scale: who and how it came, the total, then the facts as chips. */}
      <div className="pms-insp-band">
        <div className="pms-insp-client">
          <span className="pms-insp-name">
            {guest !== null ? (
              <>
                <strong>{guest.lastName.toUpperCase()}</strong> {guest.firstName}
              </>
            ) : (
              who
            )}
          </span>
          <span className="pms-insp-meta">
            {guest !== null && guest.lang.toUpperCase()}
            {r?.channel != null && r.channel !== '' && ` · ${r.channel}`}
            {r?.groupName != null && r.groupName !== '' && (
              <>
                {' · '}
                <strong>{r.groupName}</strong>
              </>
            )}
          </span>
        </div>
        {r !== null && (
          <div className="pms-insp-total" aria-label="Total">
            {fmtEuros(r.totalCents)}
            {roomCount > 1 && <span className="pms-insp-total-k">{roomCount} logements</span>}
          </div>
        )}
      </div>

      {/* The BOOKING at a glance — the pop-up's chips, same figures. */}
      <div className="pms-insp-chips" aria-label="Résumé">
        {m?.bookingAvgCents != null && (
          <span className="pms-insp-chip" title="Hébergement seul (sans taxe de séjour ni extras), par nuit-logement">
            {fmtEuros(m.bookingAvgCents)} / nuit moy.
          </span>
        )}
        {m?.boardMix.map((b) => (
          <span key={b.board} className={`pms-insp-chip pms-board-text is-${b.board}`}>
            {roomCount > 1 ? `${b.count} ` : ''}
            {PMS_BOARD_LABELS[b.board]}
          </span>
        ))}
        {m !== null && m.bookingTaxNights > 0 && (
          <span className="pms-insp-chip" title="Taxe de séjour : nuitées taxables et montant">
            TS {m.bookingTaxNights} nuitée{m.bookingTaxNights > 1 ? 's' : ''} / {fmtEuros(m.bookingTaxCents)}
          </span>
        )}
        {r?.noShow === true && <span className="pms-insp-chip is-warn">No-show</span>}
        {r !== null && r.guarantee !== 'none' && (
          <span className="pms-insp-chip">{PMS_GUARANTEE_LABELS_FR[r.guarantee]}</span>
        )}
      </div>

      {/* What has been paid is the BOOKING's, not this logement's: it sits with the
          booking-level figures, above the room it was clicked from. « Arrhes » is
          only what Vega recorded as arrhes; every other payment is named apart. */}
      {r !== null && paymentSection(advance, r.totalCents)}

      {/* Vega's two records of the group disagree — its charges against its room
          allocation. A flag, never a correction: the total above is Vega's (planGapView). */}
      {r !== null && planGap !== null && (
        <div className="pms-insp-sec">
          {typeof planGap === 'string' ? (
            <span className="pms-insp-v is-muted">Contrôle de la facturation Vega non lu : {planGap}</span>
          ) : (
            <span className="pms-insp-v is-warn" role="status" title={planGapTitle(planGap)}>
              {planGapText(planGap)} — {planGapNote(planGap)}
            </span>
          )}
        </div>
      )}

      {/* THIS logement — the line of the pop-up's room table for the bar clicked. */}
      <div className="pms-insp-sec">
        <span className="pms-insp-k">
          {roomCount > 1 ? `Ce logement — ${stay.roomNumber ?? 'à attribuer'}` : 'Séjour'}
        </span>
        <div className="pms-insp-grid">
          <Field
            k="Dates"
            v={`${fmtDayShort(stay.checkinDate)} → ${fmtDayShort(stay.checkoutDate)} · ${nights} nuit${nights > 1 ? 's' : ''}`}
          />
          <Field k="Personnes" v={peopleFr(stay.adults, stay.children, stay.infants)} />
          <Field k="Formule" v={m?.board == null ? '—' : BOARD_LONG_FR[m.board]} />
          <Field k="Prix / nuit" v={nightText ?? (money === null ? '…' : '—')} title={mark?.title ?? null} />
          {m !== null && <Field k="Hébergement" v={fmtEuros(m.roomCents)} />}
          {m !== null && m.taxCents > 0 && <Field k="Taxe de séjour" v={fmtEuros(m.taxCents)} />}
          {m !== null && m.extrasCents > 0 && <Field k="Extras" v={fmtEuros(m.extrasCents)} />}
          {m !== null && roomCount > 1 && <Field k="Total logement" v={fmtEuros(m.totalCents)} />}
        </div>
        {typeof money === 'string' && <span className="pms-insp-v is-warn">Prix non lus : {money}</span>}
      </div>

      {guest !== null && (guest.phone !== null || guest.email !== null) && (
        <div className="pms-insp-sec pms-insp-grid">
          {guest.phone !== null && <Field k="Téléphone" v={guest.phone} />}
          {guest.email !== null && <Field k="E-mail" v={guest.email} />}
        </div>
      )}

      {/* The other logements, grouped by dates and status — a 25-room group
          is one line of room numbers, not 24 identical lines. */}
      {siblings.length > 0 && (
        <div className="pms-insp-sec">
          <span className="pms-insp-k">
            Autres logements ({siblings.length}){partial === null ? '' : ` — ${partial}`}
          </span>
          {siblingGroups(siblings).map((g) => (
            <div className="pms-insp-sibs" key={g.key}>
              <span className="pms-insp-sibs-h">
                {fmtDate(g.checkin)} → {fmtDate(g.checkout)} · {PMS_STATUS_LABELS_FR[g.status]}
              </span>
              <span className="pms-insp-rooms">
                {g.rooms.map((n, i) => (
                  <span key={`${n}|${i}`} className="pms-insp-room">
                    {n}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* The three facts the desk asked to see (operator, 2026-09-30): the bed
          the guest wants, when they said they would come, and what was said. */}
      <div className="pms-insp-sec pms-insp-grid">
        <span className="pms-insp-f">
          <span className="pms-insp-k">Lit demandé</span>
          <span className={`pms-insp-v ${sel.bed.warn ? 'is-warn' : ''}`}>
            {sel.bed.warn ? '⚠ ' : ''}
            {sel.bed.text}
          </span>
        </span>
        <Field k="Arrivée prévue" v={sel.arrival ?? '—'} />
      </div>

      {sel.tetris !== null &&
        ('unavailable' in sel.tetris ? (
          <div className="pms-insp-sec">
            <span className="pms-insp-k">Où la placer — aide à l'attribution</span>
            <span className="pms-insp-v is-muted">{sel.tetris.unavailable}</span>
          </div>
        ) : (
          tetrisBody(sel.tetris)
        ))}

      {r !== null && (
        <div className="pms-insp-sec pms-insp-grid">
          <Field k="Origine" v={sel.origin ?? '—'} />
          {r.channelRef !== null && <Field k="Réf. canal" v={r.channelRef} />}
          {r.bookingPartyCode !== null && <Field k="Donneur d'ordre" v={r.bookingPartyCode} />}
        </div>
      )}

      {r?.notes != null && r.notes !== '' && (
        <div className="pms-insp-sec">
          <span className="pms-insp-k">Note</span>
          <span className="pms-insp-text">{r.notes}</span>
        </div>
      )}

      {/* The channel's own words — evidence, edited by nobody — kept apart
          from the staff note above so neither is mistaken for the other. */}
      {r?.sourceText != null && r.sourceText.trim() !== '' && (
        <div className="pms-insp-sec">
          <span className="pms-insp-k">Texte du canal</span>
          <span className="pms-insp-text is-quote">{r.sourceText}</span>
        </div>
      )}

      <div className="pms-insp-acts">
        {onOpen !== null && <span className="pms-hint">Double-clic sur la barre : ouvre la fiche.</span>}
        {onOpen === null ? (
          <span className="pms-hint">Réservation non chargée.</span>
        ) : (
          <button type="button" className="pms-chip is-primary" onClick={onOpen}>
            Ouvrir la fiche
          </button>
        )}
      </div>
    </>
  );
}

function tetrisBody(t: RackTetris) {
  return (
    <div className="pms-insp-sec">
      <span className="pms-insp-k">Où la placer — aide à l'attribution</span>
      {t.lines.length === 0 ? (
        t.plan === null ? (
          <span className="pms-insp-v is-warn">Aucun logement libre sur tout le séjour.</span>
        ) : (
          planBody(t.plan)
        )
      ) : (
        <ol className="pms-insp-props">
          {t.lines.map((l) => (
            <li key={l.roomId}>
              {/* A preview, not an action: it moves the ghost on the grid. */}
              <button
                type="button"
                className={`pms-insp-prop ${l.roomId === t.previewRoomId ? 'is-on' : ''} ${l.warn ? 'is-warn' : ''}`}
                aria-pressed={l.roomId === t.previewRoomId}
                onClick={() => t.onPreview(l.roomId)}
              >
                <b>{l.roomNumber}</b>
                <span className="pms-insp-prop-why">
                  {l.reasons.join(' · ')}
                  {l.hidden ? ' · masqué par le filtre' : ''}
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
      {t.more > 0 && (
        <span className="pms-hint">
          + {t.more} autre{t.more > 1 ? 's' : ''} possible{t.more > 1 ? 's' : ''}
        </span>
      )}
      {t.rejectedText !== null && <span className="pms-hint">{t.rejectedText}</span>}
      {/* Why there is no « Attribuer » here: until cutover the room comes from
          Vega every 30 minutes, so a room set in the PMS would be put back. */}
      <span className="pms-hint">À attribuer dans Vega — le planning le reprend à la synchronisation suivante.</span>
    </div>
  );
}

/** v2: no room is free throughout, so the plan that opens one — or, failing
 *  that, the split, or the shortfall. Each step can be vetoed for the stay it
 *  moves; the plan is recomputed without it. */
function planBody(p: RackPlanControl) {
  const v = p.view;
  return (
    <div className="pms-insp-plan">
      <span className={`pms-insp-v ${v.kind === 'plans' && v.bends.length === 0 ? '' : 'is-warn'}`}>
        Aucun logement libre sur tout le séjour.{' '}
        {v.kind === 'plans' ? 'En déplaçant :' : ''} {v.headline}
        {v.count > 1 ? ` — plan ${v.index + 1}/${v.count}` : ''}
      </span>
      {v.steps.length > 0 && (
        <ol className="pms-insp-steps">
          {v.steps.map((s, i) => (
            <li key={i} className={s.warn ? 'is-warn' : ''}>
              <span>{s.text}</span>
              {s.stayId !== null && (
                <button
                  type="button"
                  className="pms-insp-veto"
                  title="Ne pas déplacer cette réservation — chercher sans elle"
                  onClick={() => p.onVeto(s.stayId!)}
                >
                  ne pas déplacer
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
      {v.bends.length > 0 && (
        <div className="pms-insp-bends">
          <span className="pms-insp-k">Règles levées — surbooking</span>
          {v.bends.map((b) => (
            <span key={b} className="pms-insp-v is-warn">
              {b}
            </span>
          ))}
        </div>
      )}
      <div className="pms-insp-plan-acts">
        {p.onNext !== null && (
          <button type="button" className="pms-chip" onClick={p.onNext}>
            Plan suivant
          </button>
        )}
        {p.vetoed.length > 0 && (
          <button type="button" className="pms-chip" onClick={p.onClearVetoes} title="Lever les « ne pas déplacer »">
            Sans exclusion ({p.vetoed.map((x) => x.name).join(', ')})
          </button>
        )}
      </div>
    </div>
  );
}

function upgradesBody(r: RackUpgradeReview, onClose: () => void) {
  return (
    <>
      {head('Surclassements', `arrivées ${fmtDayShort(r.date)}`, '', onClose)}
      <div className="pms-insp-sec">
        <span className="pms-insp-k">
          Demi-pension, puis petit-déjeuner, puis séjours longs — en gardant {r.keepFree} chambres supérieures libres
        </span>
        {r.lines.length === 0 ? (
          <span className="pms-insp-v is-muted">Aucun surclassement à proposer.</span>
        ) : (
          <ol className="pms-insp-props">
            {r.lines.map((l) => (
              <li key={l.stayId} className="pms-insp-up">
                {/* A preview, not an action: it ghosts the upgrade on the grid. */}
                <button
                  type="button"
                  className={`pms-insp-prop ${l.stayId === r.previewStayId ? 'is-on' : ''}`}
                  aria-pressed={l.stayId === r.previewStayId}
                  onClick={() => r.onPreview(l.stayId)}
                >
                  <span className="pms-insp-prop-why">
                    {l.text} · reste {l.freeAfter} sup. libre{l.freeAfter > 1 ? 's' : ''}
                  </span>
                </button>
                <button
                  type="button"
                  className="pms-insp-veto"
                  title="Ne pas surclasser cette réservation"
                  onClick={() => r.onVeto(l.stayId)}
                >
                  non
                </button>
              </li>
            ))}
          </ol>
        )}
        {r.note !== null && <span className="pms-hint">{r.note}</span>}
        {r.vetoed.length > 0 && (
          <button type="button" className="pms-chip" onClick={r.onClearVetoes}>
            Sans exclusion ({r.vetoed.map((x) => x.name).join(', ')})
          </button>
        )}
        {/* Advice until cutover, like every proposal here. */}
        <span className="pms-hint">À faire dans Vega — le planning le reprend à la synchronisation suivante.</span>
      </div>
    </>
  );
}

function roomBody(
  sel: Extract<RackSelection, { kind: 'room' }>,
  onBook: (() => void) | null,
  onClose: () => void,
) {
  const { row, date } = sel;
  return (
    <>
      {head(row.roomNumber, row.categoryLabel ?? 'sans catégorie', date === null ? '—' : fmtDayShort(date), onClose)}

      {/* The glyphs, in words. The strip is 164px wide and reads at a glance
          precisely because it is pictograms — which is also why something has
          to spell them out somewhere, and a tooltip cannot be read next to the
          room it belongs to. */}
      <div className="pms-insp-sec pms-insp-grid">
        <Field k="Lits" v={bedConfigsLabel(row.bedLayouts, row.doubleBedType, row.currentBedConfig)} />
        <Field k="Vue" v={outlookLabel(row.isSuperior, row.view)} />
        <Field k="Bain" v={row.hasShower ? 'Douche à l’italienne' : 'Baignoire'} />
        <Field k="WC" v={row.wcNotSeparated ? 'Non séparé' : 'Séparé'} />
        <Field k="Personnes max" v={row.maxOccupancy === null ? '—' : String(row.maxOccupancy)} />
        <Field k="Étage" v={String(row.floor)} />
      </div>

      <div className="pms-insp-sec pms-insp-grid">
        <Field k="Zone chauffage" v={row.heatingZone === null ? '—' : `Zone ${row.heatingZone}`} />
        <Field
          k="Zone SdB"
          v={row.bathroomHeatingZone === null ? '—' : `Zone ${row.bathroomHeatingZone}`}
        />
      </div>

      {row.remarks !== null && row.remarks !== '' && (
        <div className="pms-insp-sec">
          <span className="pms-insp-k">Remarque</span>
          <span>{row.remarks}</span>
        </div>
      )}

      <div className="pms-insp-acts">
        {date !== null && onBook !== null && (
          // Operator L4 preserved: the click already carries the unit AND the
          // night, so this opens a form with both filled and nothing to re-pick.
          <button type="button" className="pms-chip is-primary" onClick={onBook}>
            Réserver la nuit du {fmtDate(date)}
          </button>
        )}
      </div>
    </>
  );
}
